import React from 'react';
import { Alert, AlertTitle, Box, Button, Collapse, Link } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Subscription } from '../../types';
import { getDaysUntilExpiration, isExpired, isExpiringSoon } from '../../utils/subscription-helpers';

interface SubscriptionExpirationWarningsProps {
  subscriptions: Subscription[];
  onManageSubscription?: (subscription: Subscription) => void;
}

const SubscriptionExpirationWarnings: React.FC<SubscriptionExpirationWarningsProps> = ({
  subscriptions,
  onManageSubscription
}) => {
  // Separate subscriptions by state:
  // 1. Pending cancellation - cancelled but not yet expired (can be reactivated)
  const pendingCancellationSubs = subscriptions.filter(isExpiringSoon);

  // 2. Expired with teams assigned - need to reassign teams so users aren't blocked
  const expiredWithTeamsSubs = subscriptions.filter(
    (sub) => isExpired(sub) && (sub.assignedTeamCount ?? 0) > 0
  );

  if (pendingCancellationSubs.length === 0 && expiredWithTeamsSubs.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 3 }}>
      {/* Expired Subscriptions with Teams - Critical Alert */}
      {expiredWithTeamsSubs.length > 0 && (
        <Collapse in={true}>
          <Alert
            severity="error"
            icon={<ErrorOutlineIcon />}
            sx={{ mb: 2 }}
          >
            <AlertTitle>Teams Assigned to Expired Subscription{expiredWithTeamsSubs.length > 1 ? 's' : ''}</AlertTitle>
            {expiredWithTeamsSubs.map((sub) => {
              const daysAgo = Math.abs(getDaysUntilExpiration(sub) || 0);
              const teamCount = sub.assignedTeamCount ?? 0;
              return (
                <Box key={sub.id || sub._id} sx={{ mb: 1 }}>
                  <strong>{sub.name}</strong> expired {daysAgo} day{daysAgo !== 1 ? 's' : ''} ago.
                  {' '}{teamCount} team{teamCount !== 1 ? 's are' : ' is'} assigned to this subscription and cannot be used.
                  {onManageSubscription && (
                    <>
                      {' '}
                      <Link
                        component="button"
                        variant="body2"
                        onClick={() => onManageSubscription(sub)}
                        sx={{ color: 'inherit', textDecoration: 'underline' }}
                      >
                        Reassign teams
                      </Link>
                    </>
                  )}
                </Box>
              );
            })}
          </Alert>
        </Collapse>
      )}

      {/* Pending Cancellation Subscriptions - Warning Alert */}
      {pendingCancellationSubs.length > 0 && (
        <Collapse in={true}>
          <Alert
            severity="warning"
            icon={<WarningAmberIcon />}
            sx={{ mb: 2 }}
          >
            <AlertTitle>Subscription{pendingCancellationSubs.length > 1 ? 's' : ''} Pending Cancellation</AlertTitle>
            {pendingCancellationSubs.map((sub) => {
              const daysUntil = getDaysUntilExpiration(sub) || 0;
              return (
                <Box key={sub.id || sub._id} sx={{ mb: 1 }}>
                  <strong>{sub.name}</strong> will expire in {daysUntil} day{daysUntil !== 1 ? 's' : ''}.
                  {onManageSubscription && (
                    <>
                      {' '}
                      <Link
                        component="button"
                        variant="body2"
                        onClick={() => onManageSubscription(sub)}
                        sx={{ color: 'inherit', textDecoration: 'underline' }}
                      >
                        Reactivate subscription
                      </Link>
                    </>
                  )}
                </Box>
              );
            })}
          </Alert>
        </Collapse>
      )}
    </Box>
  );
};

export default SubscriptionExpirationWarnings;
